/**
 * Centralized date formatting utilities for consistent Romanian locale formatting
 */

/**
 * Formats a date as a simple date string (e.g., "05.11.2025")
 */
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("ro-RO");
};

/**
 * Formats time as a simple time string (e.g., "14:30")
 */
export const formatTime = (date: string | Date | null | undefined): string => {
  if (!date) return "";
  return (
    " " +
    new Date(date).toLocaleTimeString("ro-RO", {
      hour: "2-digit",
      minute: "2-digit",
    })
  );
};

/**
 * Formats time with seconds (e.g., "14:30:25")
 */
export const formatTimeWithSeconds = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "-";
  return new Date(date).toLocaleTimeString("ro-RO", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

/**
 * Formats a date and time in short format with seconds (e.g., "05.11.2025, 14:30:25")
 */
export const formatDateTimeWithSeconds = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "-";
  return new Date(date).toLocaleString("ro-RO", {
    dateStyle: "short",
    timeStyle: "medium",
  });
};

export const formatDateTimeWithMilliseconds = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "-";
  const d = new Date(date);
  const datePart = d.toLocaleDateString("ro-RO", { dateStyle: "short" });
  const timePart = d.toLocaleTimeString("ro-RO", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const msPart = String(d.getMilliseconds()).padStart(3, "0");
  return `${datePart} ${timePart}.${msPart}`;
};

/**
 * Formats a date in long format (e.g., "5 noiembrie 2025")
 */
export const formatDateLong = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("ro-RO", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * Formats a date in short format (e.g., "5 nov. 2025")
 */
export const formatDateShort = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("ro-RO", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Formats a date as year and month only (e.g., "noiembrie 2025")
 */
export const formatDateYearMonth = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("ro-RO", {
    year: "numeric",
    month: "long",
  });
};

/**
 * Formats a date and time in short format (e.g., "05.11.2025, 14:30")
 */
export const formatDateTime = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "-";
  return new Date(date).toLocaleString("ro-RO", {
    dateStyle: "short",
    timeStyle: "short",
  });
};

/**
 * Formats a date and time in full format (e.g., "5 noiembrie 2025, 14:30")
 */
export const formatDateTimeLong = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "-";
  return new Date(date).toLocaleString("ro-RO", {
    dateStyle: "long",
    timeStyle: "short",
  });
};

/**
 * Formats a date and time in full format with seconds (e.g., "5 noiembrie 2025, 14:30:25")
 */
export const formatDateTimeLongWithSeconds = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "-";
  return new Date(date).toLocaleString("ro-RO", {
    dateStyle: "long",
    timeStyle: "medium",
  });
};

/**
 * Formats a date to ISO string format for API calls
 */
export const formatDateISO = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "";
  return new Date(date).toISOString();
};

/**
 * Formats a date to ISO date only (YYYY-MM-DD) for input fields
 */
export const formatDateInputValue = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "";
  return new Date(date).toISOString().split("T")[0];
};

/**
 * Calculates relative time string (e.g., "acum 2 ore", "în 5 minute")
 * Used by DateTimeRelative component for consistent Romanian relative time formatting
 * @param date - The target date to compare with now
 * @param nowOrShort - Optional current date to use for calculation (defaults to new Date()). If boolean, treated as short format flag.
 * @param shortFlag - Optional boolean to indicate short format if nowOrShort is a Date
 * @returns A string representing the relative time in Romanian
 */
export const calculateRelativeTime = (
  date: string | Date | null | undefined,
  options: {
    now?: Date;
    short?: boolean;
    disablePrefix?: boolean;
  } = {},
): string => {
  if (!date) return "-";

  // Determine parameters: (date, now?) or (date, short?) or (date, now, short)
  let now: Date;
  let short = false;
  if (options.now instanceof Date) {
    now = options.now;
  } else {
    now = new Date();
  }
  if (typeof options.short === "boolean") {
    short = options.short;
  }

  const targetDate = new Date(date);
  const diffMs = now.getTime() - targetDate.getTime();
  const isFuture = diffMs < 0;
  const absDiffMs = Math.abs(diffMs);

  const absDiffSeconds = Math.floor(absDiffMs / 1000);
  const absDiffMinutes = Math.floor(absDiffSeconds / 60);
  const absDiffHours = Math.floor(absDiffMinutes / 60);
  const absDiffDays = Math.floor(absDiffHours / 24);
  const absDiffWeeks = Math.floor(absDiffDays / 7);
  const absDiffMonths = Math.floor(absDiffDays / 30);
  const absDiffYears = Math.floor(absDiffDays / 365);

  const prefix = options.disablePrefix ? "" : isFuture ? "în " : "acum ";

  if (short) {
    if (absDiffMinutes < 60) return `${absDiffMinutes}m`;
    if (absDiffHours < 24) return `${absDiffHours}h`;
    if (absDiffDays < 30) return `${absDiffDays}z`;
    const months = Math.floor(absDiffDays / 30);
    return `${months}l`;
  }

  if (absDiffSeconds < 60) {
    if (absDiffSeconds <= 3) return "fix acum";
    return `${prefix}${absDiffSeconds} secunde`;
  } else if (absDiffMinutes < 60) {
    if (absDiffMinutes === 1) return `${prefix}1 minut`;
    return `${prefix}${absDiffMinutes} minute`;
  } else if (absDiffHours < 24) {
    if (absDiffHours === 1) return `${prefix}1 oră`;
    return `${prefix}${absDiffHours} ore`;
  } else if (absDiffDays < 7) {
    if (absDiffDays === 1) return `${prefix}1 zi`;
    return `${prefix}${absDiffDays} zile`;
  } else if (absDiffDays < 30) {
    if (absDiffWeeks === 1) return `${prefix}1 săptămână`;
    return `${prefix}${absDiffWeeks} săptămâni`;
  } else if (absDiffMonths < 12) {
    if (absDiffMonths === 1) return `${prefix}1 lună`;
    return `${prefix}${absDiffMonths} luni`;
  } else {
    if (absDiffYears === 1) return `${prefix}1 an`;
    return `${prefix}${absDiffYears} ani`;
  }
};

/**
 * Formats the absolute part of a notification timestamp.
 * If the date is today, returns only the time (e.g., "14:30").
 * If the date is on a different day, returns date and time (e.g., "05.11.2025, 14:30").
 */
export const formatSmartDate = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "";
  const d = new Date(date);
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (isToday) {
    return d.toLocaleTimeString("ro-RO", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return d.toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" });
};

export const formatDuration = (
  durationSeconds: number | null | undefined,
): string => {
  if (durationSeconds == null) return "-";
  const days = Math.floor(durationSeconds / 86400);
  const hours = Math.floor((durationSeconds % 86400) / 3600);
  const minutes = Math.floor((durationSeconds % 3600) / 60);
  const seconds = durationSeconds % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}z`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

  return parts.join(" ");
};

/**
 * Converts a duration given in seconds to an ISO 8601 duration string.
 * Examples:
 * - 90 -> "PT1M30S"
 * - 3600 -> "PT1H"
 * - 90061 -> "P1DT1H1M1S"
 */
export const formatDurationISO = (
  durationSeconds: number | null | undefined,
): string => {
  if (durationSeconds == null || Number.isNaN(Number(durationSeconds)))
    return "";
  let seconds = Math.floor(Number(durationSeconds));

  const days = Math.floor(seconds / 86400);
  seconds = seconds % 86400;
  const hours = Math.floor(seconds / 3600);
  seconds = seconds % 3600;
  const minutes = Math.floor(seconds / 60);
  seconds = seconds % 60;

  let iso = "P";
  if (days > 0) iso += `${days}D`;

  // Time component
  if (hours > 0 || minutes > 0 || seconds > 0) {
    iso += "T";
    if (hours > 0) iso += `${hours}H`;
    if (minutes > 0) iso += `${minutes}M`;
    if (seconds > 0) iso += `${seconds}S`;
  }

  // If everything is zero, represent as PT0S
  if (iso === "P") iso = "PT0S";

  return iso;
};
